'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { FilterState } from './FindCreatorClient';

const categoryOptions = ['Lifestyle', 'Beauty', 'Tech', 'Food', 'Fashion', 'Comedy', 'Sport', 'Family', 'Gaming', 'Travel', 'Education', 'Automotive', 'Business', 'UGC'];
const platformOptions = ['instagram', 'tiktok', 'youtube', 'snapchat'];
const platformLabels: Record<string, string> = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', snapchat: 'Snapchat' };

interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onReset: () => void;
}

export default function FilterPanel({ filters, onChange, onReset }: Props) {
  const [expanded, setExpanded] = useState(true);

  const toggle = <K extends keyof FilterState>(key: K, value: string) => {
    const arr = filters[key] as string[];
    onChange({
      ...filters,
      [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
    });
  };

  return (
    <div className={`flex-shrink-0 transition-all duration-300 ${expanded ? 'w-56' : 'w-12'}`}>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          {expanded && <span className="text-sm font-700 text-foreground">Filtres</span>}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all ml-auto"
          >
            <Icon name={expanded ? 'ChevronLeftIcon' : 'AdjustmentsHorizontalIcon'} size={16} />
          </button>
        </div>

        {expanded && (
          <div className="p-4 space-y-5 max-h-[calc(100vh-280px)] overflow-y-auto">
            {/* Search */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Recherche</label>
              <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 border border-border focus-within:border-primary transition-colors">
                <Icon name="MagnifyingGlassIcon" size={14} className="text-muted-foreground" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => onChange({ ...filters, search: e.target.value })}
                  placeholder="Nom, handle..."
                  className="bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none flex-1 w-full"
                />
              </div>
            </div>

            {/* Categories */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Catégorie</label>
              <div className="flex flex-wrap gap-1.5">
                {categoryOptions.map((cat) => (
                  <button
                    key={`fcat-${cat}`}
                    onClick={() => toggle('categories', cat)}
                    className={`px-2 py-1 rounded-full text-xs font-500 transition-all ${
                      filters.categories.includes(cat)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Platforms */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Plateforme</label>
              <div className="space-y-1.5">
                {platformOptions.map((p) => (
                  <label key={`fplat-${p}`} className="flex items-center gap-2 cursor-pointer group">
                    <div
                      onClick={() => toggle('platforms', p)}
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                        filters.platforms.includes(p)
                          ? 'bg-primary border-primary' :'border-border group-hover:border-primary/50'
                      }`}
                    >
                      {filters.platforms.includes(p) && (
                        <Icon name="CheckIcon" size={10} className="text-white" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                      {platformLabels[p]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Genre</label>
              <div className="flex gap-2">
                {[{ id: 'gender-all', val: '', label: 'Tous' }, { id: 'gender-f', val: 'F', label: 'Femme' }, { id: 'gender-m', val: 'M', label: 'Homme' }].map((g) => (
                  <button
                    key={g.id}
                    onClick={() => onChange({ ...filters, gender: g.val })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-500 transition-all ${
                      filters.gender === g.val
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Verified */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Certification</label>
              <div className="flex gap-2">
                {[
                  { id: 'ver-all', val: null, label: 'Tous' },
                  { id: 'ver-yes', val: true, label: '✓ Vérifié' },
                  { id: 'ver-no', val: false, label: 'Non vérifié' },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => onChange({ ...filters, verified: v.val })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-500 transition-all ${
                      filters.verified === v.val
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Available */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">Disponibilité</label>
              <div className="flex gap-2">
                {[
                  { id: 'avail-all', val: null, label: 'Tous' },
                  { id: 'avail-yes', val: true, label: 'Disponible' },
                ].map((a) => (
                  <button
                    key={a.id}
                    onClick={() => onChange({ ...filters, available: a.val })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-500 transition-all ${
                      filters.available === a.val
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Min Engagement */}
            <div>
              <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide block mb-2">
                Engagement min : <span className="text-primary">{filters.minEngagement}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={15}
                step={0.5}
                value={filters.minEngagement}
                onChange={(e) => onChange({ ...filters, minEngagement: parseFloat(e.target.value) })}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0%</span>
                <span>15%</span>
              </div>
            </div>

            {/* Reset */}
            <button
              onClick={onReset}
              className="w-full py-2 rounded-lg text-xs font-600 text-muted-foreground hover:text-foreground bg-muted hover:bg-border transition-all"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>
    </div>
  );
}